import * as THREE from "three";
import { ARCH_SCALE } from "./views/common";
import { Uniforms, isLit, patchShader } from "./unitFxShader";

/**
 * The unit "uber-shader" (art review units F1 + VFX F2) and the small pieces of maths the unit views share.
 *
 * Every enemy, boss and troop view owns a `UnitLook`: a per-view clone of each material its model draws with, patched
 * in `onBeforeCompile`. The clones all produce the same program source and report the same `customProgramCacheKey`, so
 * three.js compiles ONE program per source material and every clone reuses it — but three calls `onBeforeCompile` per
 * material (WebGLRenderer.getProgram keeps `programs` on the material's own properties), so each clone hands the program
 * its own uniform objects, and because the clones are distinct materials three re-uploads their uniforms whenever it
 * switches between them. Net cost: no extra draws, no extra programs, a few uniform uploads per unit.
 *
 * The patch lands on what is actually rendered: HQ units are rigid rigs (rigidRig.ts), one `SkinnedMesh` per material,
 * so patching the materials of the meshes found under the view's body reaches every part at once. The `_glow` parts are
 * `MeshBasicMaterial` (hqMaterials.ts) and get the reduced patch (flash + dissolve), or they would hang in the air
 * after the body has dissolved.
 *
 * Uniforms (all per view):
 * - `uTdFlash`   0–1, hit flash: the lit colour mixes toward warm white ×2.2 at 0.75 × flash. Set to 1 on a hit,
 *                decays with exp(−dt/0.06) (`flashDecay`).
 * - `uTdRim`     rgb + strength: team rim pow(1 − N·V, 3) added to the emissive. Enemies #ff5a3c 0.45, troops #6cc8ff
 *                0.7 (M30: they blended into the ground), bosses stronger and pulsing (`UnitLook.update`).
 * - `uTdStatus`  (slow, burn, poison, boost) each 0–1: slow desaturates 50 % toward #a8d8ff with a frost fresnel, burn
 *                is a flickering #ff6a1a emissive weighted to the feet, poison a #7cff5a tint pulsing at 2 Hz, boost a
 *                gold rim.
 * - `uTdDissolve` 0–1 (units F4): value noise in the model's own space (so a spinning, launched body does not swim
 *                through its pattern), weighted so the feet go first; fragments below the threshold are discarded and
 *                a band `uTdEdgeW` wide above it glows in the archetype colour `uTdEdge` (the bloom pass catches it).
 * - `uTdH`       the model's height in its own units, to normalise the noise scale and the feet weighting.
 *
 * Squash (units F5) is NOT a vertex uniform, deliberately: the brief's `transformed.y *= 1 − k; xz *= 1 + k/2` about
 * the feet is exactly a non-uniform scale of the view's body node, whose origin is the feet. Done on the node it costs
 * nothing on the GPU and — unlike a vertex patch — also reaches the shadow pass, which draws every rigid rig with one
 * shared depth material (`RigidRig.depth`) that this patch never sees.
 */
export class UnitFx {
  /** Seconds, shared by every unit look (burn flicker, poison pulse); the enemy view advances it once per frame. */
  static readonly time = { value: 0 };
  /** Program-cache suffixes: one program per source material for all clones. */
  static readonly LitKey = "td-unit-lit-v3";
  static readonly BasicKey = "td-unit-basic-v3";
}

/** What a unit is, for its rim, ring and bar colours. */
export type UnitTeam = "enemy" | "troop" | "boss";

/** Team rim colours and strengths (units F1). Bosses start stronger and pulse (see `UnitLook.update`). */
export const TEAM_RIM: Record<UnitTeam, { color: number; strength: number }> = {
  enemy: { color: 0xff5a3c, strength: 0.45 },
  troop: { color: 0x6cc8ff, strength: 0.7 },   // M30 #23: half again the enemies', so our soldiers lift off any ground
  boss: { color: 0xff5a3c, strength: 0.7 },
};

/**
 * Dissolve edge colour per archetype (units F4.1): the Ashen Horde's ember for every enemy (art/unitkit/STYLE_GUIDE.md),
 * green for the healers and the plague lord (their ability colour), cold blue for troops.
 */
export function dissolveEdge(archId: string, team: UnitTeam): number {
  if (team === "troop") return 0x8fd8ff;
  if (archId === "healer" || archId === "boss_plague_lord") return 0x6cff4a;
  return 0xff7a2a;
}

/** Per-archetype enemy scale (units F2; the table lives in views/common.ts). Unknown bosses take the boss default. */
export function archScale(archId: string): number {
  const s = ARCH_SCALE[archId];
  if (s !== undefined) return s;
  return archId.startsWith("boss_") ? ARCH_SCALE.boss : 1;
}

/** Hit flash decay: 1 → 0 with a 60 ms time constant (units F1), frame-rate independent. */
export function flashDecay(v: number, dt: number): number { return v * Math.exp(-dt / 0.06); }

/** Spawn pop (units quick wins): scale 0 → 1.1 over the first 0.2 s (ease-out), settling to 1 by 0.3 s. */
export function popScale(t: number): number {
  if (t <= 0) return 0;
  if (t < 0.2) { const u = t / 0.2; return 1.1 * (1 - (1 - u) * (1 - u)); }
  if (t < 0.3) { const u = (t - 0.2) / 0.1; return 1.1 - 0.1 * u * u * (3 - 2 * u); }
  return 1;
}

/**
 * Footfall squash (units F5): k = 0.07·max(0, cos 4πp) for the walk loop's phase p ∈ [0,1) — two thumps per stride,
 * each contact flattening the body 7 % and widening it half that.
 */
export function footfallSquash(phase: number): number { return 0.07 * Math.max(0, Math.cos(4 * Math.PI * phase)); }

/**
 * One step of a critically damped spring pulling `s.x` (and its velocity `s.v`) back to 0, exact for any dt — the
 * knockback's return (units F5). ω = 22 settles a 5 cm shove in ~0.2 s with no overshoot.
 */
export function springStep(s: { x: number; v: number }, omega: number, dt: number): void {
  const e = Math.exp(-omega * dt);
  const t = s.v + omega * s.x;
  const x = (s.x + t * dt) * e;
  s.v = (s.v - omega * t * dt) * e;
  s.x = x;
}

/**
 * Ballistic launch for blast kills (units F4.2): gravity 9, one bounce at 30 % restitution, then it lies still.
 * Integrates `b` in place; returns true once it has come to rest.
 */
export interface Ballistic { x: number; y: number; z: number; vx: number; vy: number; vz: number; bounced: boolean; rest: boolean }
export const LAUNCH = { away: 1.6, up: 2.2, spin: 9, gravity: 9, restitution: 0.3 } as const;
export function ballisticStep(b: Ballistic, floorY: number, dt: number): boolean {
  if (b.rest) return true;
  b.vy -= LAUNCH.gravity * dt;
  b.x += b.vx * dt; b.y += b.vy * dt; b.z += b.vz * dt;
  if (b.y <= floorY && b.vy < 0) {
    b.y = floorY;
    if (!b.bounced) { b.bounced = true; b.vy = -b.vy * LAUNCH.restitution; b.vx *= 0.5; b.vz *= 0.5; }
    else { b.vx = b.vy = b.vz = 0; b.rest = true; }
  }
  return b.rest;
}

/**
 * One unit view's patched materials and their uniforms. Build once per pooled view; call `update` every frame.
 */
export class UnitLook {
  readonly u: Uniforms;
  /** Current status intensities (slow, burn, poison, boost), eased toward `target` in `update`. */
  readonly status = new THREE.Vector4();
  readonly target = new THREE.Vector4();
  /** Rim base colour and strength; Phase 2 drives the boss's phase change through these (white-hot rim at 50 % hp). */
  readonly rimColor = new THREE.Color();
  rimStrength = 0.45;
  /** Boss rim pulse amplitude (0 for grunts and troops). */
  rimPulse = 0;
  /**
   * Rim heat 0–1: pulls the rim toward white-hot and adds up to +1.2 strength, decaying over ~0.6 s (the boss's
   * phase change and death). Set with `heatUp`.
   */
  heat = 0;
  private readonly team: UnitTeam;
  private readonly meshes: { mesh: THREE.Mesh; cast: boolean }[] = [];
  private readonly pairs: { clone: THREE.Material; source: THREE.Material }[] = [];

  constructor(body: THREE.Object3D, team: UnitTeam, height: number, private readonly stockEdge: number) {
    const edge = stockEdge;
    this.u = {
      uTdFlash: { value: 0 }, uTdRim: { value: new THREE.Vector4() }, uTdStatus: { value: this.status }, uTdDissolve: { value: 0 }, uTdCrack: { value: 0 },
      uTdEdgeW: { value: team === "boss" ? 0.12 : 0.06 }, uTdEdge: { value: new THREE.Color(edge) }, uTdH: { value: Math.max(0.05, height) }, uTdTime: UnitFx.time,
      uTdShiny: { value: new THREE.Vector4() }, uTdShinyA: { value: new THREE.Color() }, uTdShinyB: { value: new THREE.Color() },
    };
    this.team = team;
    this.resetRim();
    const clones = new Map<THREE.Material, THREE.Material>();
    body.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh || Array.isArray(mesh.material)) return;
      const src = mesh.material as THREE.Material;
      let c = clones.get(src);
      if (!c) { c = this.cloneFor(src); clones.set(src, c); }
      mesh.material = c;
      // Only a boss casts into the sun's shadow map. The level refreshes that map a few times a second (SceneRig manual
      // shadow updates), so a walking grunt's shadow would step behind it; its ground ring is its shadow (blobShadow.ts).
      // A boss is the exception: the rig refreshes faster while one is alive (SceneRig.BossShadowHz).
      const cast = mesh.castShadow && team === "boss";
      mesh.castShadow = cast;
      this.meshes.push({ mesh, cast });
    });
  }

  private cloneFor(src: THREE.Material): THREE.Material {
    const lit = isLit(src);
    if (!lit && !(src as THREE.MeshBasicMaterial).isMeshBasicMaterial) return src;   // unknown kinds stay as they are
    const c = src.clone();
    const prev = src.onBeforeCompile;                   // Material.clone drops the hooks (hqMaterials.ts `twin`)
    const u = this.u;
    c.onBeforeCompile = (sh, r) => { prev.call(src, sh, r); patchShader(sh, u, lit); };
    const key = `${src.customProgramCacheKey()}|${lit ? UnitFx.LitKey : UnitFx.BasicKey}`;
    c.customProgramCacheKey = () => key;
    this.pairs.push({ clone: c, source: src });
    return c;
  }

  /** Re-reads the look knobs `HqMaterials.applyLook` sets on the sources (IBL strength, normal depth), on reuse. */
  syncLook(): void {
    for (const { clone, source } of this.pairs) {
      const s = source as THREE.MeshStandardMaterial, c = clone as THREE.MeshStandardMaterial;
      if (!s.isMeshStandardMaterial) continue;
      if (c.envMapIntensity !== s.envMapIntensity) c.envMapIntensity = s.envMapIntensity;
      if (s.normalMap && !c.normalScale.equals(s.normalScale)) c.normalScale.copy(s.normalScale);
    }
  }

  /** Frees the per-view material clones (the sources belong to the asset library and stay). */
  dispose(): void { for (const { clone } of this.pairs) clone.dispose(); this.pairs.length = 0; }

  /** Back to a fresh unit: no flash, no status, fully solid, shadows restored. */
  reset(): void {
    this.u.uTdFlash.value = 0;
    this.u.uTdDissolve.value = 0;
    this.u.uTdCrack.value = 0;
    this.u.uTdShiny.value.set(0, 0, 0, 0);                // a pooled boss view may have been a rare's
    this.heat = 0;
    this.resetRim();
    this.status.set(0, 0, 0, 0);
    this.target.set(0, 0, 0, 0);
    this.setShadow(true);
    this.syncLook();
  }

  /** The team's rim colour, strength and pulse (a pooled boss view may have been re-rimmed by its phase change). */
  resetRim(): void {
    const rim = TEAM_RIM[this.team];
    this.rimColor.set(rim.color);
    this.rimStrength = rim.strength;
    this.rimPulse = this.team === "boss" ? 0.25 : 0;
  }

  /** Makes the rim flare white-hot (1 = full), decaying back over ~0.6 s. */
  heatUp(amount = 1): void { this.heat = Math.max(this.heat, Math.min(1, amount)); }

  /** Glowing cracks spreading from the chest (boss death), 0–1. */
  set crack(v: number) { this.u.uTdCrack.value = Math.max(0, Math.min(1, v)); }
  get crack(): number { return this.u.uTdCrack.value; }

  flash(amount = 1): void { this.u.uTdFlash.value = Math.max(this.u.uTdFlash.value, amount); }

  set dissolve(v: number) {
    this.u.uTdDissolve.value = Math.max(0, Math.min(1, v));
    // the shadow pass uses one shared depth material that knows nothing of the dissolve: drop the caster part-way
    if (v > 0.35) this.setShadow(false);
  }
  get dissolve(): number { return this.u.uTdDissolve.value; }

  /** The dissolve edge's colour: a kill effect's own (MP-23, views/killFx.ts), or null for the unit's stock edge. */
  tintEdge(hex: number | null): void { this.u.uTdEdge.value.setHex(hex ?? this.stockEdge); }

  setShadow(on: boolean): void { for (const m of this.meshes) m.mesh.castShadow = on && m.cast; }

  /** Per frame: flash decay, status easing (≈ 0.1 s), rim with the boss pulse. */
  update(dt: number, time: number, reduced: boolean): void {
    const f = flashDecay(this.u.uTdFlash.value, dt);
    this.u.uTdFlash.value = f < 0.005 ? 0 : f;
    const k = 1 - Math.exp(-dt * 10);
    this.status.x += (this.target.x - this.status.x) * k;
    this.status.y += (this.target.y - this.status.y) * k;
    this.status.z += (this.target.z - this.status.z) * k;
    this.status.w += (this.target.w - this.status.w) * k;
    const pulse = reduced ? 0 : this.rimPulse * Math.sin(time * Math.PI * 2 * 0.6);
    const r = this.u.uTdRim.value;
    const h = this.heat;
    r.set(this.rimColor.r + (1 - this.rimColor.r) * h, this.rimColor.g + (0.95 - this.rimColor.g) * h, this.rimColor.b + (0.85 - this.rimColor.b) * h, Math.max(0, this.rimStrength + pulse + 1.2 * h));
    if (h > 0) this.heat = h < 0.01 ? 0 : h * Math.exp(-dt / 0.25);
  }
}
